import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import styled from "styled-components"
import { Button, ButtonRow, Header, NavigationalTabs, PageTitle, TopBar } from "QuorumDesign"

// constants
import urls from "app/static/frontend/imports/desktopPaths"
import { QuorumTheme } from "app/static/frontend/design-constants"
import { COUNT_NOT_AVAILABLE } from "shared/search/constants"

// helper functions
import { getPrecisionMaxThreshold, PRECISION_MIN_THRESHOLD } from "shared/imports/sharedHelperFunctions"

// selectors
import { selectIsAiSearchApplied, selectQuorumDataType } from "app/static/frontend/search-new/selectors"
import {
    selectOrderedOptionsForSearch,
    selectCurrentQdtIndex,
} from "app/static/frontend/search-new/components/SearchBar/selectors"
import * as sharedSelectors from "shared/search/selectors"
import { selectBulkActionsCount, selectIsBulkActionsSubmittable } from "shared/search/searchify/bulkactions/selectors"

// action creators
import { segueToLink } from "app/static/frontend/help-center/help-center-action-creators"
import { setMobileFiltersOpenStatus, toggleSearchCountPrecision } from "app/static/frontend/search-new/action-creators"
import { popPage } from "global-action-creators"

import { useResponsiveSize } from "@/hooks"

import "app/static/stylus/SearchHeader.styl"

// QuorumDesign/src/Components/rules.js
// QuorumDesign/src/Components/Compounds/TopBar/style.js
// derived from above and to match on mobile for "ff_pwa_q2" changes.
const TopBarPaddingV2 = "16px"
const MobileFilterContainer = styled.div`
    align-items: center;
    display: flex;
    padding: 10px ${TopBarPaddingV2};
`

const TabsContainer = styled.div`
    padding: 0px ${TopBarPaddingV2};

    // adjust/override NavigationalTabs so as not to touch it's style file
    & > :first-child {
        padding: 0px;
        margin-bottom: 0px;
        height: auto;
        border-bottom: transparent;
        width: 70%;

        & > div {
            padding: 0.75rem 1rem 0rem;

            & > div {
                padding-bottom: 0.75rem;
                font-size: 0.875rem;
            }
        }
    }
`
const TabsBorderBottom = styled.div`
    margin: 0px ${TopBarPaddingV2};
    border-bottom: 1px solid ${QuorumTheme.colors.gray[2]};
    margin-bottom: 1rem;
`

const mapStateToProps = (state, props) => ({
    areMobileSearchFiltersOpen: sharedSelectors.selectAreMobileSearchFiltersOpen(state, props),
    abbrevCount: sharedSelectors.selectAbbreviatedCurrentCount(state, props),
    bulkActionsCount: selectBulkActionsCount(state, props),
    countIsLoading: sharedSelectors.selectIsCountLoading(state, props),
    currentCount: sharedSelectors.selectCurrentCount(state, props),
    currentExactCount: sharedSelectors.selectCurrentExactCount(state, props),
    displayLabel: sharedSelectors.selectSearchQDTLabel(state, props),
    isAiSearchApplied: selectIsAiSearchApplied(state, props),
    isBulkActions: selectIsBulkActionsSubmittable(state, props),
    isExactCountInaccessible: sharedSelectors.selectIsExactCountInaccessible(state, props),
    orderedOptions: selectOrderedOptionsForSearch(state, props),
    currentQdtIndex: selectCurrentQdtIndex(state, props),
    searchQdt: selectQuorumDataType(state, props),
    shouldShowExactCount: sharedSelectors.selectShouldShowExactCount(state, props),
    tooltipLabel: sharedSelectors.selectSearchCountTooltipLabel(state, props),
})

const actions = {
    popPage,
    segueToLink,
    setMobileFiltersOpenStatus,
    toggleSearchCountPrecision,
}

const SearchHeader = (props) => {
    const { isMobile } = useResponsiveSize()

    // This is janky, need to refactor!
    const precisionMaxThreshold = props.searchQdt ? getPrecisionMaxThreshold(props.searchQdt) : 1000000
    // request the exact count if the estimated count is between the max and min threshold
    useEffect(() => {
        if (props.currentCount < precisionMaxThreshold && props.currentCount > PRECISION_MIN_THRESHOLD) {
            props.toggleSearchCountPrecision(
                props.currentExactCount,
                props.isExactCountInaccessible,
                props.shouldShowExactCount,
                DjangIO.newResourceFromDataType(props.searchQdt),
                props.uniqueFlvId,
            )
        }
    }, [props.currentCount])

    const { orderedOptions, shouldShowExtraOption } = props.orderedOptions

    const getButtonRowBreakpoints = () => {
        if (shouldShowExtraOption) {
            return {
                iconResponsiveBreakpoint: "1150px",
                titleBreakpoint: "1200px",
                textResponsiveBreakpoint: "1050px",
            }
        }

        return {
            iconResponsiveBreakpoint: "1150px",
            titleBreakpoint: "1200px",
            textResponsiveBreakpoint: "850px",
        }
    }
    const { iconResponsiveBreakpoint, titleBreakpoint, textResponsiveBreakpoint } = getButtonRowBreakpoints()

    const editButton = [
        {
            component: "button",
            dataCy: "button-row-settings",
            icon: "cog",
            iconSize: ".875rem",
            icon_family: "far",
            onClick: () => props.segueToLink(urls.searchbar),
            type: "ghost",
        },
    ]

    const tabOptions = orderedOptions.map((option) => ({
        dataCy: `search-tab-${option.plural}`,
        iconFamily: option.fa5_icon_family,
        icon: option.fa5_icon,
        onClick: () => props.segueToLink(option.path),
        onKeyDown: (event) => {
            if (event.key === "Enter" || event.key === " ") {
                props.segueToLink(option.path)
            }
        },
        text: option.plural,
    }))

    const getTooltipText = () => {
        if (props.currentCount === COUNT_NOT_AVAILABLE) {
            return "Counts are unavailable for custom field filters due to dataset size. Consider refining your filters for better performance."
        }

        if (props.currentCount > PRECISION_MIN_THRESHOLD) {
            return props.currentCount <= precisionMaxThreshold
                ? `${(props.currentExactCount || props.currentCount).toLocaleString()} ${props.tooltipLabel}`
                : "Too many results to display exact count."
        }
        return null
    }

    const getSearchHeaderSubtitle = () => {
        const searchHeaderSubtitle = [props.abbrevCount, props.displayLabel]

        if (props.isBulkActions && props.bulkActionsCount > 0) {
            searchHeaderSubtitle.push(` (${props.bulkActionsCount.toLocaleString()} Selected)`)
        }

        return searchHeaderSubtitle.join(" ")
    }

    // For the "temporary" mobile-only version of search datasets' filter-opening
    if (isMobile) {
        if (props.areMobileSearchFiltersOpen) {
            return null
        }

        return (
            <Header
                key="search-topbar-header"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
                <TopBar
                    backDataCy="search-topbar-back"
                    isAiSearchApplied={props.isAiSearchApplied}
                    key="search-topbar-back"
                    onClickBack={() => props.popPage()}
                    title={
                        <PageTitle
                            dataCy="SearchTopbarTitle"
                            key="search-topbar-pagetitle"
                            title={`Search ${!props.countIsLoading ? getSearchHeaderSubtitle() : "Counting..."}`}
                            titleBreakpoint={titleBreakpoint}
                            tooltipIsTiny
                            tooltipMaxWidth="400px"
                            tooltipPlacement="right"
                            tooltipText={getTooltipText()}
                        />
                    }
                />
                {!props.areMobileSearchFiltersOpen && (
                    <MobileFilterContainer>
                        <Button
                            style={{ color: QuorumTheme.colors.gray[7] }}
                            onClick={() => props.setMobileFiltersOpenStatus(true)}
                            rightIcon={!props.areMobileSearchFiltersOpen && "filter"}
                            rightIconFamily="far"
                            type="borderless"
                        />
                    </MobileFilterContainer>
                )}
            </Header>
        )
    }

    // Desktop only
    return (
        <>
            <Header key="search-topbar-header">
                <TopBar
                    backDataCy="search-topbar-back"
                    isAiSearchApplied={props.isAiSearchApplied}
                    key="search-topbar-back"
                    onClickBack={() => props.popPage()}
                    title={
                        <PageTitle
                            dataCy="SearchTopbarTitle"
                            key="search-topbar-pagetitle"
                            title={`Search ${!props.countIsLoading ? getSearchHeaderSubtitle() : "Counting..."}`}
                            titleBreakpoint={titleBreakpoint}
                            tooltipIsTiny
                            tooltipMaxWidth="400px"
                            tooltipPlacement="right"
                            tooltipText={getTooltipText()}
                        />
                    }
                >
                    <ButtonRow
                        buttonOptions={editButton}
                        iconResponsiveBreakpoint={iconResponsiveBreakpoint}
                        textResponsiveBreakpoint={textResponsiveBreakpoint}
                    />
                </TopBar>
            </Header>
            <TabsContainer>
                <NavigationalTabs
                    selectedTab={props.currentQdtIndex}
                    tabOptions={tabOptions}
                    // To fix the size of the dataset buttons' dropdown menu box
                    buttonDropdownFixedHeight={400}
                    buttonDropdownFixedWidth={320}
                />
            </TabsContainer>
            <TabsBorderBottom />
        </>
    )
}

SearchHeader.propTypes = {
    currentPath: PropTypes.string.isRequired,
    uniqueFlvId: PropTypes.string,

    // from store
    abbrevCount: PropTypes.string,
    countIsLoading: PropTypes.bool,
    currentCount: PropTypes.number,
    currentExactCount: PropTypes.number,
    displayLabel: PropTypes.string,
    isAiSearchApplied: PropTypes.bool,
    isExactCountInaccessible: PropTypes.bool,
    orderedOptions: PropTypes.shape({
        shouldShowExtraOption: PropTypes.bool,
        orderedOptions: PropTypes.arrayOf(
            PropTypes.shape({
                fa5_icon: PropTypes.string,
                id: PropTypes.string,
                path: PropTypes.string,
                plural: PropTypes.string,
                qdtLabel: PropTypes.string,
                value: PropTypes.number,
            }),
        ),
    }),
    currentQdtIndex: PropTypes.number,
    searchQdt: PropTypes.number,
    shouldShowExactCount: PropTypes.bool,
    tooltipLabel: PropTypes.string,
}

export default connect(mapStateToProps, actions)(SearchHeader)
